import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowRight, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { categoryService, productService, paramService } from '@/services/storage';
import type { ProductCategory, Product, ProductParam } from '@/types';

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [params, setParams] = useState<ProductParam[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [filters, setFilters] = useState<Record<string, string>>({});

  const categoryId = searchParams.get('category') || '';
  const subCategoryId = searchParams.get('subCategory') || '';

  useEffect(() => {
    const loadCategories = async () => {
      const cats = await categoryService.getAll();
      setCategories(cats);
      
      if (categoryId) {
        const cat = cats.find(c => c.id === categoryId);
        if (cat && cat.children && cat.children.length > 0 && !subCategoryId) {
          // URL中有父分类但无子分类 → 自动跳转第一个子分类
          const firstSub = cat.children[0];
          setSelectedCategory(categoryId);
          setSelectedSubCategory(firstSub.id);
          setSearchParams({ category: categoryId, subCategory: firstSub.id });
        } else {
          setSelectedCategory(categoryId);
          setSelectedSubCategory(subCategoryId);
        }
      }
    };
    loadCategories();
  }, [categoryId]);

  // 获取当前分类的子分类
  const currentCategory = categories.find(c => c.id === selectedCategory);
  const subCategories = currentCategory?.children || [];

  useEffect(() => {
    const loadProducts = async () => {
      let allProducts: Product[] = [];
      if (selectedSubCategory) {
        allProducts = await productService.getAll();
        const prods = allProducts.filter(p => p.subCategoryId === selectedSubCategory);
        const pars = await paramService.getByCategory(selectedSubCategory);
        setProducts(prods);
        setParams(pars);
        setFilters({});
      } else if (selectedCategory) {
        const cat = categories.find(c => c.id === selectedCategory);
        if (cat && cat.children && cat.children.length > 0) {
          setProducts([]);
          setParams([]);
          setFilters({});
        } else {
          allProducts = await productService.getByCategory(selectedCategory);
          const pars = await paramService.getByCategory(selectedCategory);
          setProducts(allProducts);
          setParams(pars);
          setFilters({});
        }
      } else {
        setProducts([]);
        setParams([]);
        setFilters({});
      }
    };
    loadProducts();
  }, [selectedCategory, selectedSubCategory, categories]);

  const handleCategoryChange = (catId: string) => {
    const cat = categories.find(c => c.id === catId);
    if (cat && cat.children && cat.children.length > 0) {
      // 有子分类 → 自动跳转到第一个子分类
      const firstSub = cat.children[0];
      setSelectedCategory(catId);
      setSelectedSubCategory(firstSub.id);
      setSearchParams({ category: catId, subCategory: firstSub.id });
    } else {
      // 无子分类
      setSelectedCategory(catId);
      setSelectedSubCategory('');
      if (catId) {
        setSearchParams({ category: catId });
      } else {
        setSearchParams({});
      }
    }
  };

  const handleSubCategoryChange = (subCatId: string) => {
    setSelectedSubCategory(subCatId);
    setSearchParams({ category: selectedCategory, subCategory: subCatId });
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  // 防御：确保 params 是数组
  const safeParams = Array.isArray(params) ? params : [];

  // Filter out non-core parameters (ordering info, pricing, comments, etc.)
  const coreParams = safeParams.filter((param) => {
    if (!param || typeof param !== 'object') return false;
    const nonCoreKeywords = ['订购', '价格', '库存', '交期', '样品', 'order', 'price', 'stock', 'delivery', 'sample', 'moq', 'comment', 'contrast'];
    const label = (param.label || '').toLowerCase();
    const key = (param.key || '').toLowerCase();
    return !nonCoreKeywords.some((keyword) => label.includes(keyword) || key.includes(keyword));
  });

  const filteredProducts = products.filter((product) => {
    return Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      const paramValue = product.parameters[key];
      if (typeof paramValue === 'string') {
        return paramValue.toLowerCase().includes(value.toLowerCase());
      }
      return String(paramValue) === value;
    });
  });

  return (
    <div className="pt-20">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              在产品中心
            </h1>
            <p className="text-xl text-blue-100">
              您可以筛选自己所需的产品
            </p>
            <p className="text-blue-200 mt-2">
              In the product center You can choose the products you need
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                onClick={() => handleCategoryChange(cat.id)}
                className={selectedCategory === cat.id ? 'bg-blue-600' : ''}
              >
                {cat.name}
              </Button>
            ))}
          </div>
          {/* Sub-category Filter */}
          {subCategories.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-4 pl-4 border-l-2 border-blue-300">
              {subCategories.map((sub) => (
                <Button
                  key={sub.id}
                  variant={selectedSubCategory === sub.id ? 'default' : 'outline'}
                  onClick={() => handleSubCategoryChange(sub.id)}
                  size="sm"
                  className={selectedSubCategory === sub.id ? 'bg-blue-500' : ''}
                >
                  {sub.name}
                </Button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Parameter Filters */}
      {selectedCategory && params.length > 0 && (
        <section className="py-6 bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-500" />
                <span className="font-medium">参数筛选</span>
              </div>
              {Object.keys(filters).length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" /> 清除筛选
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {coreParams.slice(0, 6).map((param) => (
                <div key={param.key}>
                  <label className="text-sm text-gray-500 block mb-1">
                    {param.label} {param.unit && `(${param.unit})`}
                  </label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={filters[param.key] || ''}
                    onChange={(e) => handleFilterChange(param.key, e.target.value)}
                  >
                    <option value="">不限</option>
                    {param.type === 'boolean' ? (
                      <>
                        <option value="true">是</option>
                        <option value="false">否</option>
                      </>
                    ) : (
                      Array.from(
                        new Set(
                          products
                            .map((p) => p.parameters[param.key])
                            .filter(Boolean)
                        )
                      ).map((value) => (
                        <option key={String(value)} value={String(value)}>
                          {String(value)}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Product List */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {selectedCategory ? (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <Table className="w-full min-w-max">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-bold w-[140px]">Part Number</TableHead>
                      {coreParams.map((param) => (
                        <TableHead key={param.key} className="font-bold whitespace-normal break-words min-w-[100px]">
                          {param.label}
                          {param.unit && <span className="text-xs text-gray-400 ml-1">({param.unit})</span>}
                        </TableHead>
                      ))}
                      <TableHead className="font-bold w-[80px]">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.map((product) => (
                      <TableRow key={product.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium whitespace-normal break-words">
                          <Link
                            to={`/products/${product.id}`}
                            className="text-blue-600 hover:underline"
                          >
                            {product.model}
                          </Link>
                        </TableCell>
                        {coreParams.map((param) => (
                          <TableCell key={param.key} className="text-sm text-gray-600 whitespace-normal break-words">
                            {product.parameters[param.key] !== undefined
                              ? String(product.parameters[param.key])
                              : '-'}
                          </TableCell>
                        ))}
                        <TableCell>
                          <Button asChild size="sm" variant="outline">
                            <Link to={`/products/${product.id}`}>详情</Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {filteredProducts.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  暂无符合条件的产品
                </div>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((cat) => (
                <Link key={cat.id} to={`/products?category=${cat.id}`}>
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <Badge className="mb-4">{cat.nameEn}</Badge>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{cat.name}</h3>
                      <p className="text-gray-600 mb-4">{cat.description}</p>
                      <div className="flex items-center text-blue-600 text-sm font-medium">
                        查看产品 <ArrowRight className="ml-1 h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
